import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { toast } from "sonner";
import { Loader2, Lock, ArrowLeft } from "lucide-react";
import { login, setToken } from "@/lib/api";

export default function Login() {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [loading, setLoading] = useState(false);
  const navigate = useNavigate();

  const fieldCls =
    "w-full bg-transparent border-b border-white/20 pb-3 pt-1 text-[#F5F5F0] placeholder:text-[#5a5a5a] focus:border-gold focus:outline-none transition-colors duration-300";

  const submit = async (e) => {
    e.preventDefault();
    if (!email || !password) {
      toast.error("Enter your email and password.");
      return;
    }
    setLoading(true);
    try {
      const data = await login(email, password);
      setToken(data.access_token);
      toast.success("Welcome back.");
      navigate("/admin");
    } catch (err) {
      const detail = err?.response?.data?.detail;
      toast.error(typeof detail === "string" ? detail : "Login failed. Check your credentials.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-ink flex items-center justify-center px-6" data-testid="login-page">
      <div className="noise-overlay" aria-hidden="true" />
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.7, ease: [0.22, 1, 0.36, 1] }}
        className="w-full max-w-md border border-white/10 bg-surface p-10 lg:p-12"
      >
        <a href="/" className="inline-flex items-center gap-2 text-[#8A8A8A] hover:text-gold transition-colors mb-10">
          <ArrowLeft className="w-4 h-4" />
          <span className="overline text-[#8A8A8A]">Back to site</span>
        </a>

        <div className="flex items-center gap-3 mb-2">
          <Lock className="w-4 h-4 text-gold" />
          <p className="overline">Operator Access</p>
        </div>
        <h1 className="font-serif text-4xl tracking-tight mb-8">
          LUXERIDE<span className="text-gold">NY</span>
        </h1>

        <form onSubmit={submit} className="space-y-7" data-testid="login-form">
          <div>
            <label className="overline text-[#8A8A8A] block mb-2">Email</label>
            <input
              type="email"
              data-testid="login-email"
              className={fieldCls}
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder="you@email.com"
              autoComplete="username"
            />
          </div>
          <div>
            <label className="overline text-[#8A8A8A] block mb-2">Password</label>
            <input
              type="password"
              data-testid="login-password"
              className={fieldCls}
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              placeholder="••••••••"
              autoComplete="current-password"
            />
          </div>
          <button
            type="submit"
            disabled={loading}
            data-testid="login-submit-btn"
            className="group w-full flex items-center justify-center gap-3 bg-gold text-black py-4 hover:tracking-widest hover:bg-gold-hover transition-[letter-spacing,background-color] duration-500 disabled:opacity-60"
          >
            {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : null}
            <span className="overline text-black">{loading ? "Signing in" : "Sign In"}</span>
          </button>
        </form>
      </motion.div>
    </div>
  );
}
